import PropTypes from 'prop-types';
import React from 'react';
import { WeezHeader, Statistics, Card, OnboardingCard, Icon } from '@weezevent/nacre';
import { Trans, useTranslation } from 'react-i18next';
import Category from '../../models/category';
import Inventory from '../../models/inventory';
import Zone from '../../models/zoneModel';
import Contingent from '../../models/contingentModel';
import Chart from '../../models/chart';
import LoadingCard from '../../components/loadingCard';
import { Loader } from 'semantic-ui-react';

import Style from './home.module.css';

export default function ChartHome({ organization, chartId }) {
    const { t } = useTranslation();

    const [chart, loadingChart] = Chart.useApiModel({
        organizationId: organization.id,
        id: chartId
    });
    const [categories, loadingCategories] = Category.useApiModel({
        organizationId: organization.id,
        chartId: chartId
    });
    const [inventories, loadingInventories] = Inventory.useApiModel({
        organizationId: organization.id,
        chartId: chartId
    });
    const [zones, loadingZones] = Zone.useApiModel({
        organizationId: organization.id,
        chartId: chartId
    });
    const [contingents, loadingContingents] = Contingent.useApiModel({
        organizationId: organization.id,
        chartId: chartId
    });
    const items = React.useMemo(
        () => [
            {
                name: 'categories',
                values: categories,
                loading: loadingCategories,
                href: `/ticket/O${organization.pk}/charts/${chartId}/categories/new`
            },
            {
                name: 'zones',
                values: zones,
                loading: loadingZones,
                href: `/ticket/O${organization.pk}/charts/${chartId}/zones/new`
            },
            {
                name: 'inventories',
                values: inventories,
                loading: loadingInventories,
                href: `/ticket/O${organization.pk}/charts/${chartId}/inventories/new`
            },
            {
                name: 'contingents',
                values: contingents,
                loading: loadingContingents,
                href: `/ticket/O${organization.pk}/charts/${chartId}/contingents/new`
            }
        ],
        [categories, inventories, zones, contingents, loadingCategories, loadingInventories, loadingZones, loadingContingents]
    );

    const sectionCards = React.useMemo(
        () =>
            items.map(item => {
                return {
                    title: t(`ticket.charts.chart.home.section.${item.name}-title`),
                    children: t(`ticket.charts.chart.home.section.${item.name}-subtitle_${Boolean(item.values?.length)}`),
                    checked: Boolean(item.values?.length),
                    href: item.href
                };
            }),
        [items]
    );

    return (
        <div className={Style['chart-home-container']}>
            <WeezHeader title={<Trans i18nKey="ticket.charts.chart.home.title" />}>{t('ticket.charts.chart.home.subtitle')}</WeezHeader>
            <Statistics.Group>
                {items.map(item => (
                    <Statistics
                        key={item.name}
                        subtitle={t(`ticket.charts.chart.home.${item.name}`)}
                        stat={item.loading ? <Loader active inline size="small" /> : item.values?.length}
                        moreLabel={
                            <>
                                {t(`ticket.charts.chart.home.see_${item.name}`)}
                                <Icon name="chevron-right" />
                            </>
                        }
                        moreURL={`/ticket/O${organization.id}/charts/${chartId}/${item.name}`}
                    />
                ))}
            </Statistics.Group>
            <LoadingCard loading={loadingChart} height={'200px'}>
                <div className={Style['card-content']}>
                    <div className={Style['card-content-left']}>
                        <OnboardingCard
                            title={t('ticket.charts.chart.home.section.title')}
                            icon="rocket"
                            sectionCards={sectionCards}
                            footer={
                                <a className={Style['footer-link']} href={t('ticket.charts.chart.home.need_help_link')} target="_blank" rel="noopener noreferrer">
                                    {t('ticket.charts.chart.home.need_help')}
                                </a>
                            }
                        ></OnboardingCard>
                    </div>
                    <div className={Style['card-content-right']}>
                        <Card>
                            <Card.Header>
                                <Card.Title>{t('ticket.charts.chart.home.chart.title')}</Card.Title>
                            </Card.Header>
                            <Card.Content>
                                {chart?.preview_picture ? (
                                    <img alt="chart preview" className={Style['chart-image']} src={chart?.preview_picture?.url} />
                                ) : (
                                    <Card.EmptyState
                                        icon={<Icon name="traffic-barrier" />}
                                        title={t('ticket.charts.chart.home.chart.empty-title')}
                                        subtitle={t('ticket.charts.chart.home.chart.empty-subtitle')}
                                    />
                                )}
                            </Card.Content>
                        </Card>
                    </div>
                </div>
            </LoadingCard>
        </div>
    );
}

ChartHome.propTypes = {
    chartId: PropTypes.string.isRequired,
    organization: PropTypes.object.isRequired
};
